import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  CommuneAutocompleteField,
  type BirthPlaceValue,
} from '@/components/PatientData/CommuneAutocompleteField';
import { getNirError, NirField } from '@/components/PatientData/NirField';
import { PatientStepFooter } from '@/components/PatientData/PatientStepFooter';
import { PatientPageLayout } from '@/components/PatientPageLayout';
import { usePatientDataCollectionConfig } from '@/hooks/usePatientDataCollectionConfig';
import { useSavePatientStep } from '@/hooks/useSavePatientStep';
import { RootState } from '@/types/graph';
import { scrollToTop } from '@/util/scroll';

const REQUIRED_FIELD_MESSAGE = 'Ce champ est obligatoire';

type AdministrativeErrors = {
  birthPlace?: string;
  socialSecurityNumber?: string;
};

// The form (and the commune field) initialise from the stored patient once, so
// remount them when the patient changes rather than re-syncing their state.
export const PatientAdministrative = () => {
  const patientId = useSelector(
    (state: RootState) => state.consultation.patient?.id,
  );
  return <PatientAdministrativeForm key={patientId} />;
};

const PatientAdministrativeForm = () => {
  const { patient, save, isSaving, submitError, clearSubmitError } =
    useSavePatientStep('PATIENT_ADMINISTRATIVE');
  const isSelf = useSelector(
    (state: RootState) => state.questionnaire.interlocuter !== 'OTHER',
  );
  const config = usePatientDataCollectionConfig();

  const showBirthPlace = config.BIRTH_PLACE.enabled;
  const showNir = config.SOCIAL_SECURITY_NUMBER.enabled;

  const [birthPlace, setBirthPlace] = useState<BirthPlaceValue>({
    birthPlace: patient?.birthPlace ?? null,
    birthPlaceInseeCode: patient?.birthPlaceInseeCode ?? null,
  });
  const [nir, setNir] = useState<string | null>(
    patient?.socialSecurityNumber ?? null,
  );
  const [errors, setErrors] = useState<AdministrativeErrors>({});
  // Errors the fields show on their own (commune typed but not picked, NIR
  // format on blur): the typed text never reaches the state above.
  const [localErrors, setLocalErrors] = useState<AdministrativeErrors>({});
  const hasError = [
    ...Object.values(errors),
    ...Object.values(localErrors),
  ].some(Boolean);

  useEffect(() => {
    scrollToTop();
  }, []);

  const validate = (): AdministrativeErrors => {
    const nextErrors: AdministrativeErrors = {};

    if (
      showBirthPlace &&
      config.BIRTH_PLACE.required &&
      !birthPlace.birthPlace?.trim()
    ) {
      nextErrors.birthPlace = REQUIRED_FIELD_MESSAGE;
    }

    if (showNir) {
      if (config.SOCIAL_SECURITY_NUMBER.required && !nir) {
        nextErrors.socialSecurityNumber = REQUIRED_FIELD_MESSAGE;
      } else {
        const nirError = getNirError(nir);
        if (nirError) nextErrors.socialSecurityNumber = nirError;
      }
    }

    return nextErrors;
  };

  const handleSubmit = () => {
    const nextErrors = validate();
    setErrors(nextErrors);
    clearSubmitError();
    if (Object.keys(nextErrors).length > 0 || hasError) return;

    // Only the collected fields are sent, so a field the practice does not ask
    // for is never overwritten.
    save(
      {
        ...(showBirthPlace && {
          birthPlace: birthPlace.birthPlace?.trim() || null,
          birthPlaceInseeCode: birthPlace.birthPlaceInseeCode || null,
        }),
        ...(showNir && { socialSecurityNumber: nir }),
      },
      {
        onRejectedFields: (fields) => {
          const rejectedErrors: AdministrativeErrors = {
            ...(fields.some((field) => field.startsWith('birthPlace')) && {
              birthPlace: 'Commune de naissance invalide',
            }),
            ...(fields.includes('socialSecurityNumber') && {
              socialSecurityNumber: 'Numéro de sécurité sociale invalide',
            }),
          };
          setErrors(rejectedErrors);
          return Object.keys(rejectedErrors).length > 0;
        },
      },
    );
  };

  return (
    <PatientPageLayout
      columnClassName="w-full"
      title={
        <span className="flex flex-col">
          <p className="text-2xl min-[23rem]:text-3.5xl font-semibold nxs:text-center">
            Informations administratives
          </p>
        </span>
      }
      footer={
        <PatientStepFooter
          onSubmit={handleSubmit}
          isSaving={isSaving}
          submitError={submitError}
          disabled={hasError}
        />
      }
    >
      <div className="flex flex-col w-full items-center gap-3 py-4 z-10">
        <div className="flex flex-col w-full gap-6 max-w-86">
          {showBirthPlace && (
            <CommuneAutocompleteField
              value={birthPlace}
              onChange={(value) => {
                setBirthPlace(value);
                setErrors((current) => ({ ...current, birthPlace: undefined }));
              }}
              error={errors.birthPlace}
              optional={!config.BIRTH_PLACE.required}
              isSelf={isSelf}
              sex={patient?.sex}
              onLocalErrorChange={(error) =>
                setLocalErrors((current) => ({ ...current, birthPlace: error }))
              }
            />
          )}
          {showNir && (
            <NirField
              value={nir}
              onChange={(value) => {
                setNir(value);
                setErrors((current) => ({
                  ...current,
                  socialSecurityNumber: undefined,
                }));
              }}
              error={errors.socialSecurityNumber}
              optional={!config.SOCIAL_SECURITY_NUMBER.required}
              isSelf={isSelf}
              onLocalErrorChange={(error) =>
                setLocalErrors((current) => ({
                  ...current,
                  socialSecurityNumber: error,
                }))
              }
            />
          )}
        </div>
      </div>
    </PatientPageLayout>
  );
};
